import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSaasApi, feeLabel, accountKind } from './PaymentsTab';

/* Super admin — one gym's payments: whether it collects online, which
   accounts it may use (and its default), whether it may choose itself, its
   ID under each Gympe account (a Razorpay Route linked account, or its own
   PhonePe merchant ID under Gympe's partner account), fee plan, link/reminder
   settings, whether the app collects for drop-in bookings (and how long an
   unpaid one holds its seat), the WhatsApp templates that gate sending, and
   what online collection has done so far. Every change is audited. */

interface Account {
  id: string; label: string; providerLabel: string; owner: 'platform' | 'gym'; ownerGymId: string | null;
  /** collect: Gympe collects and pays out (split); direct: settles straight to the gym. */
  mode?: 'direct' | 'collect';
  environment: 'test' | 'live'; status: 'active' | 'disabled';
}
interface Config {
  enabled: boolean; allowedAccountIds: string[]; defaultAccountId: string | null; allowGymOverride: boolean;
  gymChoiceAccountId: string | null; feePlanId: string | null; payoutSchedule: string; linkExpiryDays: number;
  duesReminderOffsets: number[];
  /** The app collects for paid drop-in bookings (otherwise the desk does). */
  collectBookingsOnline: boolean;
  /** How long an unpaid online booking holds its seat, in minutes (5–60). */
  bookingPaymentMinutes: number;
}
interface AccountLink { platformAccountId: string; linkedAccountId: string | null; kycStatus: 'pending' | 'active' | 'rejected' }
interface Audit { id: string; actorType: string; createdAt: string; before: any; after: any }
interface Summary {
  collected: { count: number; amount: number };
  openLinks: number;
  fees: Record<string, number>;
  payable: Record<string, number>;
  excessToRefund: Array<{ paymentRequestId: string; amount: number; paidAt: string }>;
  templates: { payment_link: boolean; payment_overdue: boolean };
  /** Whether members can buy in the app, and if not, why. */
  store: { open: boolean; reason: 'feature_off' | 'payments_unavailable' | 'invoicing_unavailable' | null };
}
interface FeePlan { id: string; name: string; percentBps: number; fixedPaise: number; minPaise: number | null; maxPaise: number | null; isActive: boolean }

const EMPTY: Config = {
  enabled: false, allowedAccountIds: [], defaultAccountId: null, allowGymOverride: false, gymChoiceAccountId: null,
  feePlanId: null, payoutSchedule: 'weekly', linkExpiryDays: 7, duesReminderOffsets: [1, 3, 7],
  collectBookingsOnline: false, bookingPaymentMinutes: 15,
};
const card = 'bg-white rounded-xl border border-gray-200 p-5';
const input = 'w-full rounded-lg border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500';
const money = (n = 0) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GymPaymentsTab({ gymId, token }: { gymId: string; token: string }) {
  const api = useSaasApi(token);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [feePlans, setFeePlans] = useState<FeePlan[]>([]);
  const [config, setConfig] = useState<Config>(EMPTY);
  const [saved, setSaved] = useState<Config>(EMPTY);
  const [links, setLinks] = useState<AccountLink[]>([]);
  const [history, setHistory] = useState<Audit[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, f, c, s] = await Promise.all([
        api<Account[]>(`/payments/accounts?ownerGymId=${gymId}`),
        api<FeePlan[]>('/payments/fee-plans'),
        api<{ config: Config | null; accountLinks: AccountLink[]; history: Audit[] }>(`/payments/gyms/${gymId}/config`),
        api<Summary>(`/payments/gyms/${gymId}/summary`),
      ]);
      setAccounts(a); setFeePlans(f);
      const cfg = { ...EMPTY, ...(c.config ?? {}) };
      setConfig(cfg); setSaved(cfg);
      setLinks(c.accountLinks); setHistory(c.history); setSummary(s);
    } catch (e: any) {
      toast.error(e.message);
    }
  }, [api, gymId]);
  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => JSON.stringify(config) !== JSON.stringify(saved), [config, saved]);
  const allowed = accounts.filter(a => config.allowedAccountIds.includes(a.id));
  const platformAllowed = allowed.filter(a => a.owner === 'platform');

  const toggleAllowed = (id: string) => setConfig(c => {
    const on = c.allowedAccountIds.includes(id);
    const allowedAccountIds = on ? c.allowedAccountIds.filter(x => x !== id) : [...c.allowedAccountIds, id];
    return { ...c, allowedAccountIds, defaultAccountId: on && c.defaultAccountId === id ? null : c.defaultAccountId ?? (on ? null : id) };
  });

  const save = async () => {
    setSaving(true);
    try {
      await api(`/payments/gyms/${gymId}/config`, { method: 'PUT', body: JSON.stringify(config) });
      toast.success('Payment settings saved');
      load();
    } catch (e: any) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveLink = async (platformAccountId: string, body: Partial<AccountLink>) => {
    try {
      await api(`/payments/gyms/${gymId}/account-links/${platformAccountId}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Linked account saved');
      load();
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      {/* ── What it's doing ─────────────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Collected online" value={money(summary.collected.amount)} hint={`${summary.collected.count} payments`} />
          <Stat label="Open links" value={String(summary.openLinks)} />
          <Stat label="Fees accrued / invoiced" value={`${money(summary.fees.accrued)} / ${money(summary.fees.invoiced)}`} />
          <Stat label="Owed to gym (collect)" value={money(summary.payable.pending)} hint={summary.payable.paid ? `${money(summary.payable.paid)} paid out` : undefined} />
        </div>
      )}
      {summary?.store?.reason === 'invoicing_unavailable' && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <span className="font-semibold">The store is closed to members: this gym has no Gym Code.</span>{' '}
          A purchase in the app is invoiced when its payment arrives, and invoice numbers are built from the Gym Code — so
          the store stays closed rather than take money it cannot record. Set the code in this gym's <span className="font-medium">Settings</span> tab
          (or the gym sets it in its own Settings); the store opens straight away.
        </div>
      )}
      {summary && summary.excessToRefund.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {summary.excessToRefund.length} payment{summary.excessToRefund.length > 1 ? 's' : ''} received more than was owed
          ({money(summary.excessToRefund.reduce((s, e) => s + e.amount, 0))} total) — refund the member from the gateway dashboard.
        </div>
      )}

      {/* ── Collect online ──────────────────────────────────────── */}
      <section className={card}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Online payments</h3>
            <p className="text-sm text-gray-500">When off, the gym sees nothing new — no links, no reminders.</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="rounded" checked={config.enabled} onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} />
            Enabled
          </label>
        </div>

        <h4 className="text-sm font-semibold text-gray-900 mt-5 mb-2">Accounts this gym may use</h4>
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {accounts.length === 0 && <p className="text-sm text-gray-500 p-3">No accounts yet — add one on the platform Payments tab.</p>}
          {accounts.map(a => {
            const isAllowed = config.allowedAccountIds.includes(a.id);
            return (
              <div key={a.id} className="flex items-center gap-3 p-3">
                <input type="checkbox" className="rounded" checked={isAllowed} onChange={() => toggleAllowed(a.id)} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.label}</p>
                  <p className="text-xs text-gray-500">
                    {a.providerLabel} · {accountKind(a).detail} · {a.environment}
                    {a.status === 'disabled' ? ' · disabled' : ''}
                  </p>
                </div>
                <label className={`flex items-center gap-1.5 text-xs ${isAllowed ? 'text-gray-700' : 'text-gray-300'}`}>
                  <input type="radio" name="default-account" disabled={!isAllowed} checked={config.defaultAccountId === a.id}
                    onChange={() => setConfig(c => ({ ...c, defaultAccountId: a.id }))} />
                  Default
                </label>
              </div>
            );
          })}
        </div>

        <label className="flex items-center gap-2 text-sm mt-4">
          <input type="checkbox" className="rounded" checked={config.allowGymOverride} onChange={e => setConfig(c => ({ ...c, allowGymOverride: e.target.checked }))} />
          Let the gym choose between its allowed accounts (e.g. "Collect via Gympe" or "My own account")
        </label>
        {config.allowGymOverride && config.gymChoiceAccountId && (
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Gym currently chose: {accounts.find(a => a.id === config.gymChoiceAccountId)?.label ?? 'an account no longer allowed (default applies)'}
          </p>
        )}

        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <label className="text-sm">Fee plan
            <select className={input} value={config.feePlanId ?? ''} onChange={e => setConfig(c => ({ ...c, feePlanId: e.target.value || null }))}>
              <option value="">Tier default</option>
              {feePlans.filter(p => p.isActive || p.id === config.feePlanId).map(p => <option key={p.id} value={p.id}>{p.name} · {feeLabel(p)}</option>)}
            </select>
          </label>
          <label className="text-sm">Link valid for (days)
            <input className={input} type="number" min={1} max={60} value={config.linkExpiryDays}
              onChange={e => setConfig(c => ({ ...c, linkExpiryDays: Number(e.target.value) }))} />
          </label>
          <label className="text-sm">Overdue reminders on day
            <input className={input} value={config.duesReminderOffsets.join(', ')} placeholder="1, 3, 7"
              onChange={e => setConfig(c => ({ ...c, duesReminderOffsets: e.target.value.split(',').map(x => parseInt(x.trim(), 10)).filter(n => !Number.isNaN(n)) }))} />
          </label>
        </div>

        <BookingCollection config={config} onChange={patch => setConfig(c => ({ ...c, ...patch }))} />

        <div className="flex justify-end gap-2 mt-5">
          {dirty && <button onClick={() => setConfig(saved)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">Discard</button>}
          <button onClick={save} disabled={!dirty || saving} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </section>

      {/* ── The gym's ID under each Gympe account ────────────────── */}
      {platformAllowed.length > 0 && (
        <section className={card}>
          <h3 className="text-base font-semibold text-gray-900">Gym's account at the gateway</h3>
          <p className="text-sm text-gray-500 mb-3">
            Under a Gympe account, the gym's own ID at the gateway: its linked account where Gympe collects and pays out (e.g. a Razorpay Route
            account ID), or its merchant ID where Gympe's partner account settles straight to the gym (PhonePe). Payments are refused until it is active.
          </p>
          {platformAllowed.map(a => {
            const link = links.find(l => l.platformAccountId === a.id);
            return <LinkRow key={a.id} account={a} link={link} onSave={body => saveLink(a.id, body)} />;
          })}
        </section>
      )}

      {/* ── WhatsApp templates ──────────────────────────────────── */}
      {summary && (
        <section className={card}>
          <h3 className="text-base font-semibold text-gray-900">WhatsApp messages</h3>
          <p className="text-sm text-gray-500 mb-3">Enabled per gym from Messaging (template catalogue). Sends are audited in the gym's notification logs.</p>
          <TemplateRow on={summary.templates.payment_link} name="Payment link" detail="Lets staff send a link from a sale or due. Without it they can still copy the link." />
          <TemplateRow on={summary.templates.payment_overdue} name="Overdue reminder" detail={`Sent on day ${config.duesReminderOffsets.join(', ')} after the due date, with a link for everything owed.`} />
        </section>
      )}

      {/* ── History ─────────────────────────────────────────────── */}
      {history.length > 0 && (
        <section className={card}>
          <h3 className="text-base font-semibold text-gray-900 mb-2">Change history</h3>
          <ul className="text-xs text-gray-600 divide-y divide-gray-100">
            {history.map(h => {
              const changes = describeConfigChange(h.before, h.after, { accounts, feePlans });
              return (
                <li key={h.id} data-testid={`history-${h.id}`} className="py-2">
                  <p>
                    <span className="font-medium text-gray-900">{new Date(h.createdAt).toLocaleString('en-IN')}</span>
                    {' · '}{h.actorType === 'gym_admin' ? 'Gym' : 'Super admin'}
                  </p>
                  {changes.length === 0
                    ? <p className="text-gray-400">No change</p>
                    : (
                      <ul className="mt-0.5 space-y-0.5">
                        {changes.map(c => <li key={c}>{c}</li>)}
                      </ul>
                    )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * The booking opt-in. Off: a paid drop-in is "pay at the gym" and the desk
 * collects. On: the member pays in the app as they book; the booking holds its
 * seat for `bookingPaymentMinutes`, and an unpaid one is released to the
 * waitlist. It needs online payments on to be switched on, but never stops
 * them being switched off — bookings then go back to the desk on their own.
 */
function BookingCollection({ config, onChange }: { config: Config; onChange: (patch: Partial<Config>) => void }) {
  const canTurnOn = config.enabled || config.collectBookingsOnline;
  return (
    <div className="mt-5 border-t border-gray-100 pt-5">
      <h4 className="text-sm font-semibold text-gray-900">Drop-in bookings</h4>
      <p className="text-xs text-gray-500 mt-0.5">When a member books something their plan or pack doesn't cover.</p>

      <label className={`flex items-start gap-2 text-sm mt-3 ${canTurnOn ? '' : 'text-gray-400'}`}>
        <input
          type="checkbox"
          className="rounded mt-0.5"
          disabled={!canTurnOn}
          checked={config.collectBookingsOnline}
          onChange={e => onChange({ collectBookingsOnline: e.target.checked })}
        />
        <span>
          Collect payment in the app
          <span className="block text-xs text-gray-500">
            Members pay as they book. When off, the app says "pay at the gym" and the desk collects.
          </span>
        </span>
      </label>
      {!config.enabled && !config.collectBookingsOnline && (
        <p className="text-xs text-gray-500 mt-1 ml-6">Turn on online payments above first.</p>
      )}
      {!config.enabled && config.collectBookingsOnline && (
        <p className="text-xs text-amber-700 mt-1 ml-6">Online payments are off, so bookings go to the desk until they are back on.</p>
      )}

      {config.collectBookingsOnline && (
        <label className="text-sm block mt-3 ml-6 max-w-xs">
          Hold an unpaid booking for (minutes)
          <input
            className={input}
            type="number"
            min={5}
            max={60}
            value={config.bookingPaymentMinutes}
            onChange={e => onChange({ bookingPaymentMinutes: Number(e.target.value) })}
          />
          <span className="block text-xs text-gray-500 mt-1">
            If it isn't paid by then, the spot is released and offered to the waitlist. 5–60 minutes.
          </span>
        </label>
      )}
    </div>
  );
}

/* ── Change history ──────────────────────────────────────────────────────
   Audit rows hold the whole config before and after a save (super admin) or
   the gym's own account choice. Each changed field becomes one plain line —
   "Unpaid booking hold: 15 → 20 min" — with accounts and fee plans by name,
   so nobody has to read a key or an id to know what happened. */

const onOff = (v: unknown) => (v ? 'on' : 'off');
const days = (v: unknown) => (Array.isArray(v) && v.length ? `days ${v.join(', ')}` : 'none');

/** Labelled, in the order the tab shows them. Keys not listed here are never shown raw. */
const CONFIG_FIELDS: Array<{ key: keyof Config; label: string; show: (v: any, ctx: NameCtx) => string }> = [
  { key: 'enabled', label: 'Online payments', show: onOff },
  { key: 'defaultAccountId', label: 'Default account', show: (v, ctx) => ctx.account(v) },
  { key: 'allowGymOverride', label: 'Gym may choose its account', show: onOff },
  { key: 'gymChoiceAccountId', label: "Gym's chosen account", show: (v, ctx) => ctx.account(v) },
  { key: 'feePlanId', label: 'Fee plan', show: (v, ctx) => ctx.feePlan(v) },
  { key: 'payoutSchedule', label: 'Payout schedule', show: v => String(v ?? '—') },
  { key: 'linkExpiryDays', label: 'Link valid for', show: v => String(v ?? '—') },
  { key: 'duesReminderOffsets', label: 'Overdue reminders', show: days },
  { key: 'collectBookingsOnline', label: 'Collect drop-in bookings in the app', show: onOff },
  { key: 'bookingPaymentMinutes', label: 'Unpaid booking hold', show: v => String(v ?? '—') },
];
const UNITS: Partial<Record<keyof Config, string>> = { linkExpiryDays: ' days', bookingPaymentMinutes: ' min' };

interface NameCtx { account: (id: unknown) => string; feePlan: (id: unknown) => string }

export function describeConfigChange(
  before: Partial<Config> | null | undefined,
  after: Partial<Config> | null | undefined,
  { accounts, feePlans }: { accounts: Account[]; feePlans: FeePlan[] },
): string[] {
  const b = before ?? {};
  const a = after ?? {};
  const ctx: NameCtx = {
    account: id => (id ? accounts.find(x => x.id === id)?.label ?? 'an account no longer listed' : 'none'),
    feePlan: id => (id ? feePlans.find(x => x.id === id)?.name ?? 'a retired fee plan' : 'Tier default'),
  };
  const same = (k: keyof Config) => JSON.stringify(b[k] ?? null) === JSON.stringify(a[k] ?? null);
  const out: string[] = [];

  // Allowed accounts first: the default account only makes sense after them.
  if (!same('allowedAccountIds') && 'allowedAccountIds' in a) {
    const was = new Set(b.allowedAccountIds ?? []);
    const now = new Set(a.allowedAccountIds ?? []);
    const added = [...now].filter(id => !was.has(id)).map(ctx.account);
    const removed = [...was].filter(id => !now.has(id)).map(ctx.account);
    const parts = [added.length && `added ${added.join(', ')}`, removed.length && `removed ${removed.join(', ')}`].filter(Boolean);
    out.push(`Accounts allowed: ${parts.join('; ')}`);
  }
  for (const f of CONFIG_FIELDS) {
    if (!(f.key in a) || same(f.key)) continue;
    const unit = UNITS[f.key] ?? '';
    const from = f.key in b ? f.show(b[f.key], ctx) : null;
    const to = f.show(a[f.key], ctx);
    out.push(from === null ? `${f.label}: ${to}${unit}` : `${f.label}: ${from} → ${to}${unit}`);
  }
  return out;
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900 tabular-nums mt-1">{value}</p>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function TemplateRow({ on, name, detail }: { on: boolean; name: string; detail: string }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className={`mt-0.5 text-xs px-2 py-0.5 rounded font-medium ${on ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{on ? 'On' : 'Off'}</span>
      <div>
        <p className="text-sm font-medium text-gray-900">{name}</p>
        <p className="text-xs text-gray-500">{detail}</p>
      </div>
    </div>
  );
}

/**
 * The gym's ID under one Gympe account. Under a collect (split) account it is
 * the gym's linked account Gympe pays out to; under a partner account (PhonePe
 * TSP) it is the gym's own merchant ID, named on every call so the money
 * settles straight to the gym.
 */
function LinkRow({ account, link, onSave }: { account: Account; link?: AccountLink; onSave: (b: Partial<AccountLink>) => void }) {
  const [linkedAccountId, setLinkedAccountId] = useState(link?.linkedAccountId ?? '');
  const [kycStatus, setKycStatus] = useState<AccountLink['kycStatus']>(link?.kycStatus ?? 'pending');
  useEffect(() => { setLinkedAccountId(link?.linkedAccountId ?? ''); setKycStatus(link?.kycStatus ?? 'pending'); }, [link]);
  const partner = account.mode === 'direct';
  return (
    <div data-testid="account-link" className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_160px_auto] gap-2 items-end py-2">
      <p className="text-sm font-medium text-gray-900 truncate self-center">{account.label}</p>
      <label className="text-sm">{partner ? `${account.providerLabel} merchant ID` : 'Linked account ID'}
        <input className={input} value={linkedAccountId} onChange={e => setLinkedAccountId(e.target.value.trim())}
          placeholder={partner ? "The gym's MID" : 'acc_XXXXXXXX'} />
      </label>
      <label className="text-sm">{partner ? 'Status' : 'KYC status'}
        <select className={input} value={kycStatus} onChange={e => setKycStatus(e.target.value as AccountLink['kycStatus'])}>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="rejected">Rejected</option>
        </select>
      </label>
      <button onClick={() => onSave({ linkedAccountId: linkedAccountId || null, kycStatus })} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium">Save</button>
    </div>
  );
}
